import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { Mask } from "../../../../types";
import {
  clampAxis,
  clampMask,
  getMaskCanvasGeometry,
  isMasked,
  MIN_MASK_SIZE,
} from "../../../../helpers/maskHelper";

type Handle = "move" | "n" | "s" | "e" | "w" | "nw" | "ne" | "sw" | "se";
// Rectangles resize from all 8 handles; a circle only from the 4 axis handles.
const RECT_HANDLES: Handle[] = ["n", "s", "e", "w", "nw", "ne", "sw", "se"];
const CIRCLE_HANDLES: Handle[] = ["n", "s", "e", "w"];

// Orientation of each handle's resize axis (degrees, mod 180). Adding the item
// rotation and snapping to the nearest 45° picks the closest CSS resize cursor.
const HANDLE_ANGLE: Record<Handle, number | null> = {
  move: null,
  e: 0,
  w: 0,
  nw: 45,
  se: 45,
  n: 90,
  s: 90,
  ne: 135,
  sw: 135,
};
// Indexed by snapped angle / 45: 0°,45°,90°,135°.
const ANGLE_CURSORS = ["ew-resize", "nwse-resize", "ns-resize", "nesw-resize"];

const resizeCursor = (handle: Handle, rotation: number): string => {
  const base = HANDLE_ANGLE[handle];
  if (base == null) return "move";
  const angle = (((base + rotation) % 180) + 180) % 180;
  return ANGLE_CURSORS[Math.round(angle / 45) % 4] ?? "ew-resize";
};

const HANDLE_THICK = 3;
// Edge bar length / corner bracket arm length.
const EDGE_BAR = 22;
const CORNER_ARM = 16;
// Pointer movement (px) below which a "move" gesture is treated as a click.
const CLICK_DRAG_THRESHOLD = 4;
// How far the handles sit inside the mask edges.
const HANDLE_INSET = 3;

/**
 * Resize a circle by its radius, keeping the center fixed. The radius (px)
 * changes by the handle's outward drag; width/height are recomputed so the mask
 * stays a true pixel circle, and it's kept inside the box.
 */
const resizeCircle = (
  m: Mask,
  handle: Handle,
  dx: number,
  dy: number,
  boxW: number,
  boxH: number
): Mask => {
  const cx = m.x + m.width / 2;
  const cy = m.y + m.height / 2;
  const horizontal = handle === "e" || handle === "w";
  let rPx = horizontal ? (m.width / 2) * boxW : (m.height / 2) * boxH;
  if (handle === "e") rPx += dx * boxW;
  else if (handle === "w") rPx -= dx * boxW;
  else if (handle === "s") rPx += dy * boxH;
  else if (handle === "n") rPx -= dy * boxH;
  const maxR = Math.min(cx * boxW, (1 - cx) * boxW, cy * boxH, (1 - cy) * boxH);
  const minR = Math.min(boxW, boxH) * (MIN_MASK_SIZE / 2);
  rPx = Math.min(Math.max(rPx, minR), Math.max(maxR, minR));
  const width = (2 * rPx) / boxW;
  const height = (2 * rPx) / boxH;
  return {
    shape: "ellipse",
    x: cx - width / 2,
    y: cy - height / 2,
    width,
    height,
  };
};

/** Translate a pointer delta (in box fractions) on a handle into a new mask. */
const applyHandle = (
  m: Mask,
  handle: Handle,
  dx: number,
  dy: number,
  boxW: number,
  boxH: number
): Mask => {
  if (handle === "move") {
    const w = m.width;
    const h = m.height;
    return clampMask({
      ...m,
      x: Math.min(Math.max(m.x + dx, 0), 1 - w),
      y: Math.min(Math.max(m.y + dy, 0), 1 - h),
      width: w,
      height: h,
    });
  }
  if (m.shape === "ellipse") return resizeCircle(m, handle, dx, dy, boxW, boxH);
  let left = m.x;
  let top = m.y;
  let right = m.x + m.width;
  let bottom = m.y + m.height;
  if (handle.includes("w")) left = Math.min(Math.max(left + dx, 0), right - MIN_MASK_SIZE);
  if (handle.includes("e")) right = Math.max(Math.min(right + dx, 1), left + MIN_MASK_SIZE);
  if (handle.includes("n")) top = Math.min(Math.max(top + dy, 0), bottom - MIN_MASK_SIZE);
  if (handle.includes("s")) bottom = Math.max(Math.min(bottom + dy, 1), top + MIN_MASK_SIZE);
  return clampMask({ x: left, y: top, width: right - left, height: bottom - top });
};

export type MaskEditorProps = {
  mask: Mask;
  boxWidth: number;
  boxHeight: number;
  /** Box top-left in canvas px, and canvas size — used to keep the crop on-canvas. */
  boxLeft: number;
  boxTop: number;
  canvasWidth: number;
  canvasHeight: number;
  /** The item box element, moved live when a crop drag spills past the media edge. */
  boxRef: RefObject<HTMLElement>;
  /** Realign the item's Moveable controls + border while the box moves live. */
  requestMoveableUpdate?: () => void;
  /** Item rotation in degrees, so the resize cursors follow the rotation. */
  rotation?: number;
  /**
   * Commit a mask change. `itemDelta` (px) is the item move when dragging the
   * crop pushed past the media edge; null for resizes and pure mask pans.
   */
  onCommit: (mask: Mask, itemDelta: { x: number; y: number } | null) => void;
};

/**
 * Thick-line crop controls for the mask: a draggable rectangle with 8 resize
 * handles, the area outside it washed white (dimmed, opaque so the slide
 * background never shows through). Lives inside the media box and reports box
 * fractions. Independent of the media's corner/rotate (Moveable) controls.
 */
export const MaskEditor = ({
  mask,
  boxWidth,
  boxHeight,
  boxLeft,
  boxTop,
  canvasWidth,
  canvasHeight,
  boxRef,
  requestMoveableUpdate,
  rotation = 0,
  onCommit,
}: MaskEditorProps) => {
  const [draft, setDraft] = useState<Mask | null>(null);
  const m = draft ?? mask;
  const isEllipse = mask.shape === "ellipse";

  // Teardown for the in-flight drag's window listeners. Run on pointerup *and*
  // on unmount, so a gesture interrupted by deletion/deselection mid-drag can't
  // leak listeners or fire `onCommit` onto an item that no longer exists.
  const teardownRef = useRef<(() => void) | null>(null);
  useEffect(() => () => teardownRef.current?.(), []);

  const beginGesture = (
    onMove: (ev: PointerEvent) => void,
    commit: () => void
  ) => {
    const onUp = () => {
      teardownRef.current?.();
      commit();
    };
    teardownRef.current = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      teardownRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const startDrag = (handle: Handle) => (e: ReactPointerEvent) => {
    if (boxWidth <= 0 || boxHeight <= 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const halfW = boxWidth / 2;
    const halfH = boxHeight / 2;

    if (handle === "move") {
      // Drag the crop in canvas space, clamped to the canvas. The mask absorbs
      // the motion within the media; once it's flush against a media edge the
      // item itself moves (live, via boxRef) to carry the crop further.
      const start = mask;
      const w = start.width;
      const h = start.height;
      const cropCenterOffset = (mx: number, my: number) => {
        const u = (mx + w / 2) * boxWidth - halfW;
        const v = (my + h / 2) * boxHeight - halfH;
        return [halfW + cos * u - sin * v, halfH + sin * u + cos * v] as const;
      };
      const { extentX, extentY } = getMaskCanvasGeometry(
        start,
        boxWidth,
        boxHeight,
        rotation
      );
      const [startOffX, startOffY] = cropCenterOffset(start.x, start.y);
      const startCropX = boxLeft + startOffX;
      const startCropY = boxTop + startOffY;
      let latest = start;
      // The item position is "sticky": anchored to where it currently is, not
      // where the gesture started, so dragging back pans the mask within the
      // item first and only moves the item when the mask hits the far edge.
      let itemX = boxLeft;
      let itemY = boxTop;
      // This overlay sits on top of the media, so a plain click (no real
      // drag) never reaches the video's native controls underneath — track
      // whether the pointer actually moved so a click can be forwarded as a
      // play/pause toggle instead of committing a no-op mask move.
      let moved = false;
      const onMove = (ev: PointerEvent) => {
        // Straight-line distance the pointer has traveled since pointerdown.
        const distanceMoved = Math.hypot(
          ev.clientX - startX,
          ev.clientY - startY
        );
        if (!moved && distanceMoved > CLICK_DRAG_THRESHOLD) moved = true;
        const targetX = clampAxis(
          startCropX + (ev.clientX - startX),
          extentX,
          canvasWidth - extentX
        );
        const targetY = clampAxis(
          startCropY + (ev.clientY - startY),
          extentY,
          canvasHeight - extentY
        );
        // Mask center if the item stayed at its current position.
        const du = targetX - itemX - halfW;
        const dv = targetY - itemY - halfH;
        const u = cos * du + sin * dv;
        const v = -sin * du + cos * dv;
        const mcx = Math.min(Math.max((u + halfW) / boxWidth, w / 2), 1 - w / 2);
        const mcy = Math.min(Math.max((v + halfH) / boxHeight, h / 2), 1 - h / 2);
        latest = { ...start, x: mcx - w / 2, y: mcy - h / 2 };
        // Item moves only to cover what the (clamped) mask couldn't absorb.
        const [offX, offY] = cropCenterOffset(latest.x, latest.y);
        itemX = targetX - offX;
        itemY = targetY - offY;
        setDraft(latest);
        if (boxRef.current) {
          boxRef.current.style.transform = `translate(${itemX}px, ${itemY}px) rotate(${rotation}deg)`;
          // Keep the item's Moveable controls + border on the moving box.
          requestMoveableUpdate?.();
        }
      };
      beginGesture(onMove, () => {
        setDraft(null);
        if (!moved) {
          const video = boxRef.current?.querySelector("video");
          if (video) {
            if (video.paused)
              video
                .play()
                .catch((error) => console.error("Error playing video:", error));
            else video.pause();
          }
          return;
        }
        onCommit(latest, { x: itemX - boxLeft, y: itemY - boxTop });
      });
      return;
    }

    // Resize handles: convert the screen delta into the box's own axes.
    let latest = mask;
    const onMove = (ev: PointerEvent) => {
      const sdx = ev.clientX - startX;
      const sdy = ev.clientY - startY;
      const dx = (cos * sdx + sin * sdy) / boxWidth;
      const dy = (-sin * sdx + cos * sdy) / boxHeight;
      latest = applyHandle(mask, handle, dx, dy, boxWidth, boxHeight);
      setDraft(latest);
    };
    beginGesture(onMove, () => {
      setDraft(null);
      onCommit(latest, null);
    });
  };

  return (
    <div style={OVERLAY_STYLE}>
      <div
        data-interactive
        onPointerDown={startDrag("move")}
        style={{
          position: "absolute",
          left: `${m.x * 100}%`,
          top: `${m.y * 100}%`,
          width: `${m.width * 100}%`,
          height: `${m.height * 100}%`,
          boxSizing: "border-box",
          borderRadius: isEllipse ? "50%" : undefined,
          // Wash everything outside the mask; clipped to the box by the wrapper.
          // box-shadow follows border-radius, so the hole is circular for ellipses.
          boxShadow: "0 0 0 9999px rgba(255, 255, 255, 0.6)",
          // "move" would suggest dragging is the only thing a click here does —
          // misleading now that a plain click (no drag) toggles play/pause on
          // the underlying video instead.
          cursor: isMasked(mask) ? "pointer" : undefined,
          // At the default full-box mask this rect covers the entire item (a
          // no-op drag, since panning a full box is always clamped to zero) —
          // left non-interactive so it doesn't block native controls (e.g. a
          // video's play button) underneath until an actual crop exists.
          pointerEvents: isMasked(mask) ? "auto" : "none",
        }}
      >
        {(isEllipse ? CIRCLE_HANDLES : RECT_HANDLES).map((h) => (
          <div
            key={h}
            data-interactive
            onPointerDown={startDrag(h)}
            style={handleStyle(h, rotation)}
          />
        ))}
      </div>
    </div>
  );
};

const OVERLAY_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  zIndex: 5,
};

const handleStyle = (h: Handle, rotation: number): CSSProperties => {
  const s: CSSProperties = {
    position: "absolute",
    pointerEvents: "auto",
    boxSizing: "border-box",
    cursor: resizeCursor(h, rotation),
  };
  if (h === "n" || h === "s") {
    // Horizontal bar, centered, just inside the edge.
    s.left = "50%";
    s.transform = "translateX(-50%)";
    s.width = EDGE_BAR;
    s.height = HANDLE_THICK;
    s.background = "#fff";
    s.boxShadow = "0 0 2px rgba(0, 0, 0, 0.6)";
    if (h === "n") s.top = HANDLE_INSET;
    else s.bottom = HANDLE_INSET;
  } else if (h === "e" || h === "w") {
    // Vertical bar, centered, just inside the edge.
    s.top = "50%";
    s.transform = "translateY(-50%)";
    s.width = HANDLE_THICK;
    s.height = EDGE_BAR;
    s.background = "#fff";
    s.boxShadow = "0 0 2px rgba(0, 0, 0, 0.6)";
    if (h === "w") s.left = HANDLE_INSET;
    else s.right = HANDLE_INSET;
  } else {
    // Corner: thick L-bracket (two borders), inset inside the corner.
    s.width = CORNER_ARM;
    s.height = CORNER_ARM;
    s.filter = "drop-shadow(0 0 1px rgba(0, 0, 0, 0.7))";
    if (h.includes("n")) {
      s.top = HANDLE_INSET;
      s.borderTop = `${HANDLE_THICK}px solid #fff`;
    } else {
      s.bottom = HANDLE_INSET;
      s.borderBottom = `${HANDLE_THICK}px solid #fff`;
    }
    if (h.includes("w")) {
      s.left = HANDLE_INSET;
      s.borderLeft = `${HANDLE_THICK}px solid #fff`;
    } else {
      s.right = HANDLE_INSET;
      s.borderRight = `${HANDLE_THICK}px solid #fff`;
    }
  }
  return s;
};
